/* ===========================================================================
 * WHY A PUNCH WAS FLAGGED, IN WORDS — one reader for the screens and the files
 * (merge E6).
 *
 * Lifted from `apps/web/src/lib/flagReasons.ts`, whose `useFlagReasonLabel`
 * now calls `flagReasonText` with i18n's `t`; the API calls it with the same
 * `flagReasons.*` words carried in `EXPORT_WORDS` (held to both catalogues by
 * `apps/web/src/test/exportWords.test.ts`). So a held scan's reason in the
 * Scan approvals file is, word for word, the reason the queue draws (§3.6).
 * ======================================================================== */

/** Reason codes we have a translated label for. */
export const FLAG_REASON_CODES = [
  // Kiosk / web heuristics
  'IDENTICAL_GPS_RECENT',
  'SHARED_IP_MULTIPLE_USERS',
  'SHARED_DEVICE_MULTIPLE_USERS',
  'MOCK_LOCATION',
  // Outside the company's OPERATING REGION entirely — a different fact from
  // being outside a worksite radius, and the more serious of the two.
  'OUTSIDE_ALLOWED_REGION',
  'OUTSIDE_GEOFENCE_AUDIT_ONLY',
  // Standing nowhere near ANY worksite — the server writes this bare code when
  // a punch has coordinates but no site matched (attendance.service.ts:700).
  // It was missing from this list while `flagReasons.NO_WORKSITE_MATCH` existed
  // in BOTH locale bundles, so `flagReasonKey()` returned null and every
  // surface — the queue, the movement feed, the map popup, the employee record
  // — printed the raw enum at the reader. Rule 3.6 asks for one translated
  // reason everywhere; this was the one code that had a translation and never
  // reached it.
  'NO_WORKSITE_MATCH',
  // A CHECK-OUT let through on a fix too coarse to prove anything. A closing
  // punch is never refused for its location — refusing strands the worker on
  // the clock and leaves an open day for a human to correct — so the weak
  // evidence is recorded and flagged instead of being turned into a wall. The
  // server appends the reading as `CODE:<metres>`.
  'LOW_GPS_ACCURACY_AUDIT_ONLY',
  /* A NETWORK fix (Wi-Fi/cell) outside the fence — field.punchLocation.ts. A
   * router carried between sites keeps its old place, so this is "approximate",
   * never "N m from site centre", which read as a worker somewhere else. Sent
   * as `CODE:<metres>:<radius>`; audit-only and paid, like the distance flag. */
  'APPROX_NETWORK_FIX',
  /* HARDWARE-TERMINAL CLAIM CHECKS (ZKTeco / Hikvision).
   *
   * `terminalIngest.service.ts` writes these three and its own header says the
   * codes are "decoded for display by apps/web/src/lib/flagReasons.ts". They
   * never reached this list, so the labels sitting in BOTH locale bundles were
   * unreachable and every surface printed `TERMINAL_NOT_ASSIGNED` at the
   * reader — the same defect `NO_WORKSITE_MATCH` above is annotated with.
   *
   * It is reachable from the held-punch queue: replaying a punch held long
   * enough lands FLAGGED for being outside the 7-day backdate window, and that
   * verdict is the one thing the operator needs in words. */
  'TERMINAL_NOT_ASSIGNED',
  // On the roster, not on the day's PUBLISHED dispatch (dispatch.plan.ts).
  'TERMINAL_OFF_PLAN',
  'TERMINAL_TIME_OUT_OF_RANGE',
  'TERMINAL_NO_WORKSITE',
  // Field-device capture flags
  'MANUAL',
  'UNKNOWN_ID',
  'NO_IN',
  'NO_PHOTO',
  'ROOTED',
  'NO_GPS',
  /* WHY there was no fix — the handheld writes one beside the plain NO_GPS
   * (Geo.java REASON_*, 3.8.19), because the office acts on the reason: a
   * permission or a switch is fixed by the operator, no sky is not. */
  'NO_GPS_PERMISSION',
  'NO_GPS_OFF',
  'NO_GPS_STALE',
  'NO_GPS_NOFIX',
  /* The fix was checked and still lay far from where this tablet had just
   * been (PunchFix rule 7, APK 3.8.63): stamped as it is, and said. */
  'GPS_JUMP',
  /* The operator was told on the tablet that this face matched another
   * worker and recorded it anyway (APK 3.8.46, CaptureSameFace). */
  'SAME_FACE_OVERRIDE',
  /* A typed number whose photo scored low against that worker. The device
   * writes it beside the server's own sentence, so the scan queue printed
   * "MANUAL_FACE_LOW · Entered by number — face check low (0.59)" (owner,
   * 2026-10-03). Translated alone; dropped when the sentence already says it. */
  'MANUAL_FACE_LOW',
] as const;

export type FlagReasonCode = (typeof FLAG_REASON_CODES)[number];

const KNOWN: ReadonlySet<string> = new Set(FLAG_REASON_CODES);

/** The server's sentence for a plain typed entry (`MANUAL_ENTRY_REASON`), read as MANUAL. */
const MANUAL_SENTENCE = 'Manual worker-ID entry on field device';

/** i18n key for a reason code, or null when the code is not one we know. */
export function flagReasonKey(reason: string): string | null {
  return KNOWN.has(reason) ? `flagReasons.${reason}` : null;
}

/** A reason split into its code and any measured detail (`CODE:<metres>:<radius>`). */
export function parseFlagReason(reason: string): { code: string; distanceMeters?: number; radiusMeters?: number } {
  const [code, dist, radius] = reason.split(':');
  const m = Number(dist);
  const r = Number(radius);
  if (!code || !KNOWN.has(code) || !Number.isFinite(m) || !Number.isFinite(r)) return { code: reason };
  return { code, distanceMeters: m, radiusMeters: r };
}

/** `SAME_FACE_AS?code=…&name=…&at=HH:MM&score=0.82&event=<id>` (field.sameFace.ts), else null. */
export function parseSameFace(reason: string): { code: string; name: string; at: string; score: string; event: string | null } | null {
  const prefix = 'SAME_FACE_AS?';
  if (!reason.startsWith(prefix)) return null;
  const q = new URLSearchParams(reason.slice(prefix.length));
  const code = q.get('code');
  if (!code) return null;
  return { code, name: q.get('name') ?? '', at: q.get('at') ?? '', score: q.get('score') ?? '', event: q.get('event') };
}

/** The same-face token anywhere in a possibly-compound reason. */
export function findSameFace(reason: string | null | undefined): ReturnType<typeof parseSameFace> {
  if (!reason) return null;
  for (const part of reason.split(/\s·\s|,/)) {
    const hit = parseSameFace(part.trim());
    if (hit) return hit;
  }
  return null;
}

/** A word by key, interpolated with `vars`; null when the catalogue has no such key. */
export type FlagReasonWords = (key: string, vars?: Record<string, string | number>) => string | null;

function translateOne(code: string, word: FlagReasonWords): string {
  // The id after it is for auditors; the drawer names the approver beside it.
  if (code.startsWith('Voided by approved correction')) return word('flagReasons.VOIDED_BY_CORRECTION') ?? code;
  const sameFace = parseSameFace(code);
  if (sameFace) {
    const key = sameFace.event ? 'flagReasons.SAME_FACE_AS' : 'flagReasons.SAME_FACE_AS_enrolled';
    return word(key, { ...sameFace, event: sameFace.event ?? '' }) ?? code;
  }
  const { code: bare, distanceMeters, radiusMeters } = parseFlagReason(code);
  const key = flagReasonKey(bare);
  if (!key) return code;
  // A measured distance gets the `_detail` variant, so a reviewer can triage by magnitude.
  if (distanceMeters !== undefined) {
    return word(`${key}_detail`, { distance: distanceMeters, radius: radiusMeters ?? '' }) ?? word(key) ?? code;
  }
  return word(key) ?? code;
}

/**
 * A `flaggedReason` in words. Empty → "Flagged for review"; an unknown token
 * passes through as itself (a code we cannot name is still evidence). Tokens
 * split on commas and on the server's ` · `, translated one by one, rejoined
 * with ` · `; `NO_GPS` beside its reason, and MANUAL beside the typed-number
 * sentence, say a fact twice and are dropped.
 */
export function flagReasonText(reason: string | null | undefined, word: FlagReasonWords): string {
  if (!reason) return word('flagReasons.default') ?? '';
  const tokens = reason
    .split(/,| · /)
    .map((s) => s.trim())
    .map((s) => (s === MANUAL_SENTENCE ? 'MANUAL' : s))
    .filter(Boolean);
  if (tokens.length === 0) return reason;
  const why = tokens.some((tok) => tok.startsWith('NO_GPS_'));
  const typedSaid = tokens.some((tok) => tok.startsWith('Entered by number'));
  return [...new Set(tokens)]
    .filter((tok) => !(why && tok === 'NO_GPS'))
    .filter((tok) => !(typedSaid && (tok === 'MANUAL' || tok === 'MANUAL_FACE_LOW')))
    .map((token) => translateOne(token, word))
    .join(' · ');
}
